import { useEffect, useState } from 'react';

import { pt } from '../copy';

type ApiKeyBarProps = {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onClear: () => void;
};

export default function ApiKeyBar({ value, onChange, onSave, onClear }: ApiKeyBarProps) {
  const [expanded, setExpanded] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!value.trim()) {
      setRevealed(false);
    }
  }, [value]);

  const hasValue = value.trim().length > 0;

  if (!expanded && !hasValue) {
    return (
      <div className="bg-white/5 p-3 text-white ring-1 ring-white/10 backdrop-blur-xl rounded-2xl">
        <button
          className="rounded-full bg-white/10 px-4 py-2 text-sm font-medium transition hover:bg-white/15"
          type="button"
          onClick={() => setExpanded(true)}
        >
          {pt.apiKeyAdd}
        </button>
      </div>
    );
  }

  if (!expanded && hasValue) {
    return (
      <div className="flex items-center gap-3 bg-white/5 p-3 text-white ring-1 ring-white/10 backdrop-blur-xl rounded-2xl">
        <span className="rounded-full bg-white/10 px-3 py-1 text-sm tracking-[0.3em]">
          •••• ••••
        </span>
        <button
          className="rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium transition hover:bg-white/15"
          type="button"
          onClick={() => {
            onClear();
            setExpanded(true);
          }}
        >
          {pt.apiKeyReplace}
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md bg-white/5 p-3 text-white ring-1 ring-white/10 backdrop-blur-xl rounded-2xl">
      <div className="flex flex-col gap-2">
        <label
          className="text-xs font-medium uppercase tracking-[0.24em] text-white/60"
          htmlFor="api-key-input"
        >
          {pt.apiKeyLabel}
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            aria-label={pt.apiKeyLabel}
            autoComplete="off"
            className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-white/25"
            id="api-key-input"
            placeholder={pt.apiKeyPlaceholder}
            type={revealed ? 'text' : 'password'}
            value={value}
            onChange={(event) => onChange(event.target.value)}
          />
          <div className="flex gap-2">
            <button
              aria-pressed={revealed}
              className="rounded-xl bg-white/10 px-3 py-2 text-sm font-medium transition hover:bg-white/15"
              type="button"
              onClick={() => setRevealed((current) => !current)}
            >
              {revealed ? pt.apiKeyHide : pt.apiKeyShow}
            </button>
            <button
              className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:bg-white/20 disabled:text-white/45"
              disabled={!hasValue}
              type="button"
              onClick={() => {
                onSave();
                if (value.trim()) {
                  setExpanded(false);
                }
              }}
            >
              {pt.apiKeySave}
            </button>
          </div>
        </div>
        <p className="text-xs text-white/55">{pt.apiKeyHelper}</p>
      </div>
    </div>
  );
}
